import { Injectable } from '@angular/core';
import {
    DiscountsAndMultipliersService,
    IccDocumentPosition,
    OfferValidity,
    PriceVersion,
    Product,
    SalesDocument,
    SalesDocumentPosition,
    State,
    TaxRatesService,
} from '@icc/b2b/shared';
import { ComplexConfiguration } from '@icc/configurator/complex/configurations';
import { PricesDataService } from '@icc/shared/prices-data.service';
import { PositionService } from './position.service';
import { DocumentManagerService } from './document-manager.service';
import { changePosition } from './+state/sales-documents.actions';
import { Store } from '@ngrx/store';

type SlavePositionData = {
    product: Product;
    productPrice: any;
    productUnit: any;
    fields: {
        [fieldId: string]: number;
    };
};

@Injectable({
    providedIn: 'root',
})
export class DocumentSlavePositionsService {
    constructor(
        private pricesDataService: PricesDataService,
        private positionService: PositionService,
        private documentManagerService: DocumentManagerService,
        private discountsAndMultipliersService: DiscountsAndMultipliersService,
    ) {}

    async updateSlavePositions(
        position: SalesDocumentPosition,
        activeDocument: SalesDocument,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity,
        positions: SalesDocumentPosition[]
    ) {
        if (
            position.configuration.type === 'complex' &&
            ComplexConfiguration.is(position.configuration)
        ) {
            position.slaves = {};
            const productPrices = await this.pricesDataService.getProductPricesForPriceVersion(
                documentPriceVersion.id,
                activeDocument.buyerCompanyId
            );
            const productFields = Object.values(position.configuration.fields).filter(
                (el) => el.type === 'select_product' && el.selectedProductId
            );

            const productsMap = new Map<number, Map<null | string, SlavePositionData>>();

            for (const productField of productFields) {
                const quantityField = position.configuration.fields[productField.id + '_qty'];
                const unlinkedFieldId = productField.productUnlinked ? productField.id : null;

                if (Number(quantityField?.value) < 1) {
                    continue;
                }

                if (!productsMap.get(productField.selectedProductId)?.get(unlinkedFieldId)) {
                    let productMap = productsMap.get(productField.selectedProductId);
                    if (!productMap) {
                        productMap = new Map<null | string, SlavePositionData>();
                        productsMap.set(productField.selectedProductId, productMap);
                    }

                    const product = await this.positionService.getProduct(
                        productField.selectedProductId
                    );
                    const productPrice = productPrices[productField.selectedProductId];
                    const productUnit = productPrice?.priceUnits?.find((el) => el.isPrimary);
                    productMap.set(unlinkedFieldId, {
                        product,
                        productPrice,
                        productUnit,
                        fields: {
                            [productField.id]: Number(quantityField?.value || 0),
                        },
                    });
                } else {
                    productsMap.get(productField.selectedProductId).get(unlinkedFieldId).fields[productField.id] =
                        Number(quantityField?.value || 0);
                }
            }
            await this.createOrUpdateSlavePositions({
                masterPosition: position,
                activeDocument,
                productsMap,
                positions,
                documentPriceVersion,
                currentOfferValidity,
            });
            this.documentManagerService.savePosition(position.uuid, activeDocument.uuid, {
                slaves: position.slaves,
            });
        }
    }

    async updateSlavePositionsOnDelete(
        masterPosition: SalesDocumentPosition,
        activeDocument: SalesDocument
    ) {
        if (
            masterPosition.configuration.type === 'complex' &&
            ComplexConfiguration.is(masterPosition.configuration) &&
            Object.keys(masterPosition.slaves || {}).length > 0
        ) {
            const positions = await this.documentManagerService.getDocumentPosition(
                activeDocument.uuid
            );
            await this.removeSlavePositions(masterPosition, activeDocument, positions);
        }
    }

    async updateSlavePositionQuantity(
        masterPosition: SalesDocumentPosition,
        activeDocument: SalesDocument,
        oldPositionQuantity: number
    ) {
        if (
            masterPosition.configuration.type === 'complex' &&
            ComplexConfiguration.is(masterPosition.configuration) &&
            Object.keys(masterPosition.slaves || {}).length > 0
        ) {
            const positions = await this.documentManagerService.getDocumentPosition(
                activeDocument.uuid
            );
            const slavePositions = positions.filter(
                (el) => el.type === 'static' && (el.masters || {})[masterPosition.uuid]
            );

            for (const slavePosition of slavePositions) {
                const quantity = this.sumFieldsQuantity(slavePosition.masters[masterPosition.uuid]);
                slavePosition.quantity = slavePosition.quantity - quantity * oldPositionQuantity + quantity * masterPosition.quantity;
                this.documentManagerService.savePosition(slavePosition.uuid, activeDocument.uuid, {
                    quantity: slavePosition.quantity,
                });

                if (activeDocument.discountType === 'value' && slavePosition.type !== 'defect') {
                    await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(activeDocument, slavePosition, 'sales');
                    this.documentManagerService.savePositionPrices(
                        slavePosition.uuid,
                        activeDocument.uuid,
                        {
                            priceTaxRateId: slavePosition.priceTaxRateId,
                            priceTaxRate: slavePosition.priceTaxRate,
                            priceBeforePromotionsValue: slavePosition.priceBeforePromotionsValue,
                            pricePromotions: slavePosition.pricePromotions,
                            priceBeforeDiscountsValue: slavePosition.priceBeforeDiscountsValue,
                            priceDiscounts: slavePosition.priceDiscounts,
                            priceAfterDiscountsValue: slavePosition.priceAfterDiscountsValue,
                            priceIsGross: TaxRatesService.isGross(activeDocument.taxRateCalculationMethod),
                            priceCurrency: slavePosition.priceCurrency,
                        },
                    );
                }
            }
        }
    }

    private async createOrUpdateSlavePositions({
        masterPosition,
        activeDocument,
        productsMap,
        positions,
        documentPriceVersion,
        currentOfferValidity,
    }: {
        masterPosition: SalesDocumentPosition;
        activeDocument: SalesDocument;
        productsMap: Map<number, Map<null | string, SlavePositionData>>;
        positions: SalesDocumentPosition[];
        documentPriceVersion: PriceVersion;
        currentOfferValidity: OfferValidity;
    }) {
        masterPosition.slaves = {};
        for (const [productId, productMap] of productsMap) {
            for (const [unlinkedFieldId, productData] of productMap) {
                let slavePosition = !unlinkedFieldId && positions.find(
                    (el) =>
                        el.type === 'static' &&
                        el.configuration.productId === productId &&
                        el.priceUnitId === productData.productUnit?.id &&
                        el.priceUnitCode === productData.productUnit?.code &&
                        Object.keys(el.masters || {}).length > 0
                );
                if (slavePosition) {
                    await this.updateSlavePosition(
                        masterPosition,
                        activeDocument,
                        slavePosition,
                        productData,
                        positions
                    );
                } else {
                    slavePosition = await this.createSlavePosition(
                        !unlinkedFieldId ? masterPosition : null,
                        activeDocument,
                        productData,
                        documentPriceVersion,
                        currentOfferValidity
                    );
                }

                if (!unlinkedFieldId && slavePosition) {
                    for (const fieldId in productData.fields) {
                        if (Object.prototype.hasOwnProperty.call(productData.fields, fieldId)) {
                            masterPosition.slaves[fieldId] = {
                                positionId: slavePosition.uuid,
                                productId,
                                unitId: productData.productUnit?.id,
                                unitCode: productData.productUnit?.code,
                            };
                        }
                    }
                }
            }
        }

        await this.removeSlavePositions(masterPosition, activeDocument, positions, productsMap);
    }

    private async createSlavePosition(
        masterPosition: SalesDocumentPosition | null,
        activeDocument: SalesDocument,
        productData: SlavePositionData,
        documentPriceVersion: PriceVersion,
        currentOfferValidity: OfferValidity
    ) {
        let quantity = 0;
        for (const fieldId in productData.fields) {
            if (Object.prototype.hasOwnProperty.call(productData.fields, fieldId)) {
                quantity += productData.fields[fieldId];
            }
        }

        if (quantity < 1) {
            return null;
        }

        const unitInfo = this.pricesDataService.getUnitInfo(productData.productUnit?.id, productData.productUnit?.code);

        const slavePosition = await this.positionService.createPositionFromProduct(
            activeDocument,
            activeDocument.priceCurrency,
            productData.product,
            {
                price: {
                    value: productData.productUnit?.value,
                    currency: productData.productPrice?.currency,
                    isGross: productData.productPrice?.isGross,
                    taxRate: productData.productPrice?.taxRate,
                    resetTaxRate: productData.productPrice?.resetTaxRate,
                    unitId: productData.productUnit?.id,
                    unitCode: productData.productUnit?.code,
                },
                quantity: quantity * (masterPosition?.quantity ?? 1),
                unit: {
                    id: productData.productUnit?.id,
                    symbol: unitInfo?.symbol,
                    code: productData.productUnit?.code,
                },
            },
            documentPriceVersion,
            currentOfferValidity,
            await this.documentManagerService.getDocumentPosition(activeDocument.uuid) as SalesDocumentPosition[],
            masterPosition ? {
                positionId: masterPosition.uuid,
                fields: productData.fields,
            } : null
        );
        this.documentManagerService.addPosition(slavePosition, activeDocument);
        return slavePosition;
    }

    private async updateSlavePosition(
        masterPosition: SalesDocumentPosition,
        activeDocument: SalesDocument,
        slavePosition: SalesDocumentPosition,
        productData: SlavePositionData,
        positions: SalesDocumentPosition[]
    ) {
        slavePosition.masters[masterPosition.uuid] = productData.fields;
        slavePosition.quantity = this.sumMastersQuantity(slavePosition, positions, masterPosition);
        this.documentManagerService.savePosition(slavePosition.uuid, activeDocument.uuid, {
            masters: slavePosition.masters,
            quantity: slavePosition.quantity,
        }, false);
        await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(activeDocument, slavePosition, 'sales');
        this.documentManagerService.savePositionPrices(
            slavePosition.uuid,
            activeDocument.uuid,
            {
                priceTaxRateId: slavePosition.priceTaxRateId,
                priceTaxRate: slavePosition.priceTaxRate,
                priceBeforePromotionsValue: slavePosition.priceBeforePromotionsValue,
                pricePromotions: slavePosition.pricePromotions,
                priceBeforeDiscountsValue: slavePosition.priceBeforeDiscountsValue,
                priceDiscounts: slavePosition.priceDiscounts,
                priceAfterDiscountsValue: slavePosition.priceAfterDiscountsValue,
                priceIsGross: TaxRatesService.isGross(activeDocument.taxRateCalculationMethod),
                priceCurrency: slavePosition.priceCurrency,
            },
        );

    }

    private async removeSlavePositions(
        masterPosition: SalesDocumentPosition,
        activeDocument: SalesDocument,
        positions: IccDocumentPosition[],
        productsMap?: Map<number, Map<null | string, SlavePositionData>>
    ) {
        const oldSlavePositions = positions.filter(
            (el) => el.type === 'static' && (el.masters || {})[masterPosition.uuid]
        ) as SalesDocumentPosition[];

        for (const slavePosition of oldSlavePositions) {
            if (
                productsMap &&
                productsMap.get(slavePosition.configuration?.productId)?.get(null)?.productUnit?.id ===
                    slavePosition.priceUnitId &&
                productsMap.get(slavePosition.configuration?.productId)?.get(null)?.productUnit?.code ===
                    slavePosition.priceUnitCode
            ) {
                continue;
            }

            delete slavePosition.masters[masterPosition.uuid];
            slavePosition.quantity = this.sumMastersQuantity(slavePosition, positions, masterPosition);
            if (Object.keys(slavePosition.masters).length === 0 || slavePosition.quantity === 0) {
                this.documentManagerService.savePosition(slavePosition.uuid, activeDocument.uuid, {
                    masters: slavePosition.masters,
                    isDeleted: true,
                }, false);
            } else {
                this.documentManagerService.savePosition(slavePosition.uuid, activeDocument.uuid, {
                    masters: slavePosition.masters,
                    quantity: slavePosition.quantity,
                }, false);
                await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(activeDocument, slavePosition, 'sales');
                this.documentManagerService.savePositionPrices(
                    slavePosition.uuid,
                    activeDocument.uuid,
                    {
                        priceTaxRateId: slavePosition.priceTaxRateId,
                        priceTaxRate: slavePosition.priceTaxRate,
                        priceBeforePromotionsValue: slavePosition.priceBeforePromotionsValue,
                        pricePromotions: slavePosition.pricePromotions,
                        priceBeforeDiscountsValue: slavePosition.priceBeforeDiscountsValue,
                        priceDiscounts: slavePosition.priceDiscounts,
                        priceAfterDiscountsValue: slavePosition.priceAfterDiscountsValue,
                        priceIsGross: TaxRatesService.isGross(activeDocument.taxRateCalculationMethod),
                        priceCurrency: slavePosition.priceCurrency,
                    },
                );
            }
        }
    }

    private sumMastersQuantity(
        slavePosition: SalesDocumentPosition,
        positions: IccDocumentPosition[],
        masterPosition: SalesDocumentPosition
    ) {
        return Object.entries(slavePosition.masters).reduce((quantity, [positionId, fields]) => {
            const position = positionId === masterPosition.uuid
                ? masterPosition
                : positions.find((el) => el.uuid === positionId);
            return quantity + this.sumFieldsQuantity(fields) * (position?.quantity ?? 0);
        }, 0);
    }

    private sumFieldsQuantity(fields: { [fieldId: string]: number }) {
        let quantity = 0;
        for (const fieldId in fields) {
            if (Object.prototype.hasOwnProperty.call(fields, fieldId)) {
                quantity += fields[fieldId];
            }
        }
        return quantity;
    }
}
